import { useMemo } from 'react'
import type { ProcurementQueueItem } from '../../shared/api/types'
import { itemStatusLabel } from '../../shared/ui/Badge'
import { PRIORITY_LABELS, formatDeadline, matchesQueueRow } from './queueTypes'
import type { QueueRow } from './queueTypes'

/**
 * Строки очереди: клиентские фильтры → маппинг в QueueRow → деление на секции
 * «Новые / В работе / Обработано» (по queueSection с бэка) + счётчики
 * совпадений поиска по секциям. Вынесено из ProcurementQueuePage (≤500 строк).
 */
export function useQueueRows(params: {
  items: ProcurementQueueItem[]
  inQueue: boolean
  procStateFilter: 'NEW' | 'ACTIVE' | null
  searchText: string
  userId?: string
}) {
  const { items, inQueue, procStateFilter, searchText, userId } = params

  // Клиент-сайд фильтры: «Новые (очередь)» и procurementState (с дашборда).
  const visibleItems = useMemo(() => {
    let rows = items
    // «Новые (очередь)»: только что пришедшие (Новая) и уже взятые в очередь
    // (В очереди) — решение Сергея 16.10.2026; раньше только «В очереди».
    if (inQueue) rows = rows.filter((i) => i.itemStatus === 'NEW' || i.itemStatus === 'IN_QUEUE')
    if (procStateFilter === 'NEW') {
      rows = rows.filter((i) => (i.procurementState ?? 'NEW') === 'NEW')
    }
    if (procStateFilter === 'ACTIVE') {
      rows = rows.filter(
        (i) =>
          i.procurementState === 'REQUESTED' ||
          i.procurementState === 'WAITING' ||
          i.procurementState === 'NEEDS_INFO',
      )
    }
    return rows
  }, [items, inQueue, procStateFilter])

  const rowData = useMemo<QueueRow[]>(
    () =>
      visibleItems.map((item) => ({
        id: item.requestItemId,
        categoryLabel: item.clientCategory,
        clientCode: item.clientCode ?? '—',
        requestNumber: item.requestNumber ?? '—',
        partNumber: item.partNumber,
        brand: item.brand ?? '—',
        noteForProcurement: item.noteForProcurement ?? '',
        qtyLabel: `${item.quantity} ${item.unit ?? 'pcs'}`,
        priorityLabel: PRIORITY_LABELS[item.priority],
        deadlineLabel: formatDeadline(item.requestDeadline),
        statusLabel: itemStatusLabel(item.itemStatus),
        assigneeLabel: item.assignedToId === userId ? 'Я' : item.assignedToId ? 'назначен' : '—',
        managerLabel: item.managerName ?? '—',
        _raw: item,
      })),
    [visibleItems, userId],
  )

  // Три секции очереди (07.07.2026): «новые», «В работе» (запрос отправлен,
  // ответа нет — раньше ложно падали в «Обработано», баг M3) и «Обработано».
  const activeRowData = useMemo(
    () => rowData.filter((r) => r._raw.queueSection === 'new'),
    [rowData],
  )
  const inWorkRowData = useMemo(
    () => rowData.filter((r) => r._raw.queueSection === 'in_work'),
    [rowData],
  )
  const processedRowData = useMemo(
    () => rowData.filter((r) => r._raw.queueSection === 'processed'),
    [rowData],
  )

  // Счётчики совпадений по поиску — для скрытия пустой активной таблицы и
  // автораскрытия секций, где нашлось совпадение.
  const hasSearch = searchText.trim().length > 0
  const matches = (rows: QueueRow[]) =>
    hasSearch ? rows.filter((r) => matchesQueueRow(r, searchText)).length : rows.length
  const activeMatches = useMemo(
    () => matches(activeRowData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSearch, searchText, activeRowData],
  )
  const inWorkMatches = useMemo(
    () => matches(inWorkRowData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSearch, searchText, inWorkRowData],
  )
  const processedMatches = useMemo(
    () => matches(processedRowData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSearch, searchText, processedRowData],
  )

  return {
    rowData,
    activeRowData,
    inWorkRowData,
    processedRowData,
    hasSearch,
    activeMatches,
    inWorkMatches,
    processedMatches,
  }
}
